import { Inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { combineLatest, iif, of } from 'rxjs'
import { catchError, concatMap, filter, map, pairwise, switchMap, tap, withLatestFrom, } from 'rxjs/operators'

import { Title } from '@angular/platform-browser'
import { RouteGuardService } from '../../../shared/route-guard/route-guard.service'
import { ErrorReportingService } from '../../../shared/services/error-reporting.service'
import { DRR_BASE_HREF } from '../../../shared/shared.config'
import { ActionRequestParams, PayloadedAction } from '../../../shared/types'
import { concatAddress, dispatchErrorAction } from '../../../shared/utils'
import { closeLaunchWebview } from '../../../shared/utils/launch-webview'
import { buildTitle } from '../../../shared/utils/title'
import { AAAStore } from '../../../store/root-reducer'
import { authNoRouting } from '../../auth/auth.actions'
import { selectEligibility, selectNoRouting } from '../../auth/auth.selectors'
import { ConfigService } from '../../config/config.service'
import { TITLE_SERVICE_TRACKING } from '../../constants/shared.constants'
import {
  lookupClubByBreakdownLocation,
  setBreakdownLocationSuccess,
  setLocationDetails,
  setSpecialAssistance,
} from '../../location/location.actions'
import { selectLocationClub } from '../../location/location.selectors'
import { BreakdownLocation, parseSpecialAssistanceFromDirections } from '../../location/location.types'
import { PASSENGERS_OPTIONS } from "../../location/passengers/passengers.component"
import { completeSetTowDestination, setPassengers } from '../../location/tow-location/tow-location.actions'
import { RouteTypes } from '../../main.routes'
import { assignExistingVehicle, memberEligibilityResult } from '../../member/member.actions'
import { selectMemberData } from '../../member/member.selectors'
import { RapService } from '../../rap/rap.service'
import { AdobeEventService } from '../../tagging/adobe/event-adobe.service'
import events from '../../tagging/events'
import { trackerProviderDetail } from '../../tagging/events/dashboard'
import { AutomatedEventMeta, CallStatusTaggingParams } from '../../tagging/tagging.actions'
import { createSelectIsCallStatusTagged } from '../../tagging/tagging.selectors'
import { TaggingService } from '../../tagging/tagging.service'
import { AdobeEventTypes } from '../../tagging/tagging.types'
import { MessageDialogTypes, StepTypes } from '../../ui/ui.types'
import { selectCurrentUrl } from '../../wizard/wizard.selectors'
import { notifyCallFailure, resetCallStatusError, setCallStatusError, setCallTowing } from '../calls.actions'
import { CallsService } from '../calls.service'
import { AAACallStatus, BreakdownLocationParams, CallStatus } from '../calls.types'
import {
  ACTIVE_CALL_STATUS,
  activeCallStatusRequest,
  activeCallStatusSuccess,
  CALL_STATUS,
  callStatusRequest,
  callStatusSuccess,
  notifyActiveCallStatusFailure,
  notifyCallStatusFailure,
} from './call-status.actions'
import {
  selectActiveCallsCalled,
  selectActiveCallStatus,
  selectCallTowLocation,
  selectDisplayTrackingLink,
  selectExternalService,
} from './call-status.selectors'
import { CallStatusService } from './call-status.service'
import { EXTERNAL_SERVICES } from './call-status.types'

function tagEvent(
  tagging: TaggingService,
  meta: AutomatedEventMeta,
  adobeEventService: AdobeEventService
) {
  switch (meta.callStatus) {
    case CallStatus.REQUEST_RECEIVED:
      sendTaggingEvent({
        automatedEventValue: events.dashboard.CALL_STATUS_RECEIVED_SUCCESS,
        adobeEventValue: events.dashboard.CALL_STATUS_RECEIVED,
        meta,
        tagging,
        adobeEventService
      })
      break

    case CallStatus.EN_ROUTE:
      sendTaggingEvent({
        automatedEventValue: events.dashboard.CALL_STATUS_ENROUTE_SUCCESS,
        adobeEventValue: events.dashboard.CALL_STATUS_ENROUTE,
        meta,
        tagging,
        adobeEventService
      })
      break

    case CallStatus.ARRIVED:
      sendTaggingEvent({
        automatedEventValue: events.dashboard.CALL_STATUS_ONLOCATION_SUCCESS,
        adobeEventValue: events.dashboard.CALL_STATUS_ARRIVED,
        meta,
        tagging,
        adobeEventService
      })
      break
    case CallStatus.TOWING:
      sendTaggingEvent({
        automatedEventValue: events.dashboard.CALL_STATUS_TOWING_SUCCESS,
        adobeEventValue: events.dashboard.CALL_STATUS_TOW,
        meta,
        tagging,
        adobeEventService
      })
      break
  }
}

function sendTaggingEvent({
  automatedEventValue,
  adobeEventValue,
  meta,
  tagging,
  adobeEventService
}: CallStatusTaggingParams) {
  tagging.setAutomatedEvent(
    automatedEventValue,
    events.dashboard.CALL_STATUS_PAGE_TYPE,
    meta
  )
  adobeEventService.sendEvent({
    eventName: AdobeEventTypes.SYSTEM,
    eventValue: events.dashboard.CALL_REQUEST_STATUS
  }, null, {
    call_status: adobeEventValue
  })
}

function buildActiveCallActions(callStatus: AAACallStatus, locationClub: string): PayloadedAction[] {
  const breakdownLocation = callStatus.breakdownLocation as BreakdownLocation
  const { locationCode } = callStatus.breakdownLocation as BreakdownLocationParams
  const dispatchedActions: Array<PayloadedAction> = [
    setBreakdownLocationSuccess({
      payload: {
        ...breakdownLocation,
        address: concatAddress(breakdownLocation, null, true),
      },
    }),
    ...(locationClub ? [] : [lookupClubByBreakdownLocation()]),
    assignExistingVehicle({
      payload: callStatus.vehicle,
    }),
  ]

  if (locationCode) {
    dispatchedActions.push(setLocationDetails({ payload: { locationCode } }))
  }

  if (breakdownLocation.driverDirections) {
    const specialAssistance = parseSpecialAssistanceFromDirections(breakdownLocation.driverDirections)
    dispatchedActions.push(
      setSpecialAssistance({ payload: specialAssistance })
    )
  }

  if (callStatus.towDestination) {
    dispatchedActions.push(
      completeSetTowDestination({
        payload: callStatus.towDestination,
      })
    )
    dispatchedActions.push(
      setCallTowing({
        payload: {
          ...callStatus.towDestination,
          name: callStatus.towDestination.location,
          latitude: callStatus.towDestination.latitude,
          longitude: callStatus.towDestination.longitude,
        },
      })
    )
    const numberOfPassengers = callStatus.numberOfPassengers || 0
    const passengerOption = PASSENGERS_OPTIONS
      .find(opt => opt.value.charAt(0) === numberOfPassengers.toString())
    dispatchedActions.push(setPassengers({
      payload: passengerOption
    }))
  }

  return dispatchedActions
}

@Injectable()
export class CallsStatusesEffect {
  constructor(
    private actions$: Actions,
    private _callsService: CallsService,
    private _callStatusService: CallStatusService,
    private store$: Store<AAAStore>,
    private taggingService: TaggingService,
    private adobeEventService: AdobeEventService,
    private errorReportingService: ErrorReportingService,
    private configService: ConfigService,
    private routeGuardService: RouteGuardService,
    protected titleService: Title,
    private rapService: RapService,
    @Inject(DRR_BASE_HREF) private drrBaseHref: string
  ) {}

  handleCallsStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof callStatusRequest>>(CALL_STATUS.REQUEST),
      withLatestFrom(
        this.store$.select(selectMemberData),
        this.store$.select(selectEligibility),
      ),
      filter(
        ([action, memberData, eligibility]) => {
          if (!action.payload.eligible || action.payload.ersAbuser) {
            return true // see DRRWEB-305
          }
          const checkCallStatusRap = eligibility?.eligible
          const checkCallStatusMember = memberData?.eligible && !memberData.ersAbuser
          return checkCallStatusMember || checkCallStatusRap
        }),
      switchMap(([action]) => [activeCallStatusRequest({ payload: action.payload })])
    )
  )

  handleActiveCallStatusRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof activeCallStatusRequest>>(ACTIVE_CALL_STATUS.REQUEST),
      withLatestFrom(
        this.store$.pipe(select(selectActiveCallsCalled))
      ),
      switchMap(([action, activeCallCalled]: [PayloadedAction, boolean]) =>
        (activeCallCalled
          ? of(null)
          : this._callsService.getActiveCall$(action.payload.timeout, action.payload.addCallFailure)).pipe(
          withLatestFrom(
            this.store$.pipe(select(selectActiveCallStatus))
          ),
          map(
            ([remoteCall, localCall]): any => {
              if (action.payload.addCallFailure && !remoteCall) {
                this.adobeEventService.sendEvent({
                  eventName: AdobeEventTypes.SYSTEM,
                  eventValue: events.submit.SUMMARY_CALL_REQUEST_FAILED
                })
                this.taggingService.setAutomatedEvent(
                  events.submit.SUBMIT_FAILURE,
                  events.submit.SUBMIT_PAGE_TYPE
                )
                return notifyCallFailure({
                  payload: {
                    ignoreFailure: false
                  }
                });
              } else if (!action.payload.retry && (!action.payload.eligible || action.payload.ersAbuser) && !remoteCall) {
                return memberEligibilityResult({ payload: action.payload })
              }
              // rxjs 6 collapsed this union to `any`; rxjs 7 types it honestly.
              // getActiveCall$ yields CallDetails while the local selector yields
              // AAACallStatus -- the active-call payload carries the AAACallStatus
              // fields at runtime, CallDetails just does not declare them.
              const activeCall = (remoteCall || localCall) as AAACallStatus
              return activeCallStatusSuccess({
                payload: {
                  data: activeCall,
                  params: action.payload
                },
              })
            }
          ),
          catchError((error) => {
            if (action.payload.addCallFailure) {
              this.adobeEventService.sendEvent({
                eventName: AdobeEventTypes.SYSTEM,
                eventValue: events.submit.SUMMARY_CALL_REQUEST_FAILED,
              })
              this.taggingService.setAutomatedEvent(
                events.submit.SUBMIT_FAILURE,
                events.submit.SUBMIT_PAGE_TYPE
              )
              return this.errorReportingService.notifyErrorObservable(
                error,
                notifyCallFailure
              )
            } else {
              return this.errorReportingService.notifyErrorObservable(
                error,
                notifyActiveCallStatusFailure
              )
            }
          }
          )
        )
      )
    )
  )

  handleActiveCallStatusSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof activeCallStatusSuccess>>(ACTIVE_CALL_STATUS.SUCCESS),
      switchMap((action: PayloadedAction<{ data: AAACallStatus | null, params: ActionRequestParams }>) => {
        const basicCallInfo = action.payload.data
        if (!basicCallInfo) {
          return of(callStatusSuccess({ payload: { data: null } })).pipe(
            tap(() => {
              if (!(action.payload?.params?.retry)) {
                this._callStatusService.stopCallStatusesUpdater();
              }
            })
          )
        }

        return this._callsService.getCallDetails$(basicCallInfo.callKey, action.payload.params.retry).pipe(
          withLatestFrom(
            this.store$.pipe(select(selectActiveCallStatus)),
            this.store$.pipe(select(createSelectIsCallStatusTagged)),
            this.store$.pipe(select(selectCallTowLocation)),
          ),
          switchMap(
            ([
              remoteCallStatus,
              localCallStatus,
              selectIsCallStatusTagged,
              callTowLocation,
            ]) =>
              iif(
                () => !localCallStatus && !remoteCallStatus,
                of(callStatusSuccess({ payload: { data: null } })).pipe(
                  tap(() => {
                    if (!(action.payload?.params?.retry)) {
                      this._callStatusService.stopCallStatusesUpdater();
                    }
                  })
                ),
                of({ remoteCallStatus, localCallStatus, selectIsCallStatusTagged, callTowLocation }).pipe(
                  tap(() => {
                    this._callStatusService.startCallStatusesUpdater({
                      retry: true
                    });
                    this.store$.dispatch(resetCallStatusError());
                  }),
                  switchMap(({ remoteCallStatus: remoteCall, localCallStatus: localCall, selectIsCallStatusTagged: isCallStatusTaggedSelector, callTowLocation: towLocation }) => {
                    this.titleService.setTitle(buildTitle(TITLE_SERVICE_TRACKING(), this.rapService.isRapUser()))

                    const callStatus = remoteCall?.status || localCall?.status
                    switch (callStatus) {
                      case CallStatus.COMPLETED:
                        this._callStatusService.resetCallStatus(MessageDialogTypes.CALL_COMPLETED)
                        return of(callStatusSuccess({ payload: { data: null } }))
                      case CallStatus.CANCELLED:
                        this._callStatusService.resetCallStatus(MessageDialogTypes.CALL_CANCELLED)
                        return of(callStatusSuccess({ payload: { data: null } }))
                    }

                    if (remoteCall) {
                      const isTagged = isCallStatusTaggedSelector(remoteCall.callKey, remoteCall.status)
                      if (!isTagged) {
                        tagEvent(this.taggingService, {
                          callIdentifier: remoteCall.callKey,
                          callStatus: remoteCall.status,
                        }, this.adobeEventService)
                      }
                    }

                    const activeCall = remoteCall && ![CallStatus.CANCELLED, CallStatus.COMPLETED].includes(remoteCall.status)
                      ? remoteCall
                      : null

                    if (!activeCall) {
                      return of(callStatusSuccess({ payload: { data: null } }))
                    }

                    const towDestination = activeCall.towDestination
                    if (towDestination) {
                      const hasCoordinates = towDestination.latitude && towDestination.longitude
                      if (!hasCoordinates && towLocation) {
                        towDestination.latitude = towLocation.latitude
                        towDestination.longitude = towLocation.longitude
                      }
                    }

                    const actions = [
                      callStatusSuccess({
                        payload: { data: activeCall }
                      }),
                      notifyCallFailure({
                        payload: {
                          ignoreFailure: true
                        }
                      })
                    ]

                    return actions
                  }),
                  catchError((error) =>
                    this.errorReportingService.notifyErrorObservable(
                      error,
                      [
                        setCallStatusError(),
                        dispatchErrorAction(notifyCallStatusFailure, error)
                      ]
                    )
                  )
                )
              )
          ),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              [
                setCallStatusError(),
                dispatchErrorAction(notifyCallStatusFailure, error)
              ]
            )
          )
        )
      })
    )
  )

  handleActiveCallStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof callStatusSuccess>>(CALL_STATUS.SUCCESS),
      map((action) => action.payload.data),
      filter((activeCall) => activeCall !== null),
      withLatestFrom(this.store$.select(selectLocationClub)),
      concatMap(([activeCall, locationClub]) => {
        return buildActiveCallActions(activeCall, locationClub)
      })
    )
  )

  /**
   * Closes the Appointment Assistant webview once DRR stops showing the external tracker (the call left En Route,
   * the tracking link went away or there is no active call). Intentional exception to "the user closes the
   * webview": Appointment Assistant stops showing the map/ETA once the driver is On Location, and the modal
   * would otherwise hide DRR's own tracker until the user dismissed it. Other providers keep the user in control.
   */
  closeAppointmentAssistantWebview$ = createEffect(
    () =>
      combineLatest([
        this.store$.select(selectDisplayTrackingLink),
        this.store$.select(selectExternalService),
      ]).pipe(
        map(([displayTrackingLink, externalService]) => ({ displayTrackingLink, provider: externalService?.provider })),
        pairwise(),
        // The tracking info may already be gone when the tracker is hidden, so use the provider it was shown for.
        filter(([previous, current]) => previous.displayTrackingLink && !current.displayTrackingLink),
        map(([previous]) => previous.provider),
        filter((provider) => provider?.toUpperCase() === EXTERNAL_SERVICES.APPOINTMENT_ASSISTANT),
        concatMap((provider) => closeLaunchWebview().then((closed) => ({ closed, provider }))),
        filter(({ closed }) => closed),
        tap(({ provider }) =>
          this.adobeEventService.sendEvent({
            eventName: AdobeEventTypes.SYSTEM,
            eventValue: events.dashboard.WEBVIEW_AUTO_CLOSED,
          }, null, trackerProviderDetail(provider))
        )
      ),
    { dispatch: false }
  )

  handleNoActiveCallNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<
          | ReturnType<typeof callStatusSuccess>
          | ReturnType<typeof notifyCallStatusFailure>
        >(CALL_STATUS.SUCCESS, CALL_STATUS.FAILURE),
        withLatestFrom(
          this.store$.select(selectCurrentUrl),
          this.store$.select(selectNoRouting)
        ),
        map(([action, url, noRouting]) => [
          (action as ReturnType<typeof callStatusSuccess>).payload?.data,
          url,
          noRouting
        ]),
        filter(
          ([activeCall, url, noRouting]: [
            AAACallStatus | null,
            string,
            boolean
          ]) =>
            (!activeCall && url.includes(`/${RouteTypes.SIGNIN}`)) ||
            activeCall === null
        ),
        tap(() => this._callStatusService.stopCallStatusesUpdater()),
        map(([_, __, noRouting]) => {
          if (noRouting) {
            return this.store$.dispatch(authNoRouting({
              payload: {
                success: true,
                redirectParams: {
                  commands: [this.drrBaseHref, RouteTypes.STEPS],
                  extras: {
                    queryParams: { step: StepTypes.BREAKDOWN_LOCATION },
                    replaceUrl: true,
                  }
                }
              }
            }))
          } else {
            return this.routeGuardService.redirectToFirstStep()
          }
        })
      ),
    { dispatch: false }
  )
}
